// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	type CoretProfileCosmetics,
	type CoretShopAnimation,
	getCoretProfileCosmeticsBatch,
} from '@app/features/coret/CoretMediaApi';
import styles from '@app/features/coret/CoretProfileCosmetics.module.css';
import type React from 'react';
import {useEffect, useState} from 'react';

const cache = new Map<string, {value: CoretProfileCosmetics; expiresAt: number}>();
const pending = new Map<string, Promise<CoretProfileCosmetics>>();
const queued = new Set<string>();
const deferred = new Map<
	string,
	{resolve: (value: CoretProfileCosmetics) => void; reject: (reason: unknown) => void}
>();
const subscribers = new Map<string, Set<() => void>>();
let batchScheduled = false;
let shopListenerInstalled = false;
const COSMETICS_CACHE_TTL_MS = 5 * 60_000;

async function flushCosmeticsBatch(): Promise<void> {
	batchScheduled = false;
	const userIds = [...queued].slice(0, 100);
	for (const userId of userIds) queued.delete(userId);
	if (userIds.length === 0) return;
	try {
		const values = await getCoretProfileCosmeticsBatch(userIds);
		for (const userId of userIds) {
			const value = values[userId];
			if (!value) throw new Error(`Resposta de cosméticos ausente para ${userId}`);
			cache.set(userId, {value, expiresAt: Date.now() + COSMETICS_CACHE_TTL_MS});
			deferred.get(userId)?.resolve(value);
		}
	} catch (error) {
		for (const userId of userIds) deferred.get(userId)?.reject(error);
	} finally {
		for (const userId of userIds) {
			deferred.delete(userId);
			pending.delete(userId);
		}
		if (queued.size > 0 && !batchScheduled) {
			batchScheduled = true;
			queueMicrotask(() => void flushCosmeticsBatch());
		}
	}
}

function ensureShopListener(): void {
	if (shopListenerInstalled || typeof window === 'undefined') return;
	shopListenerInstalled = true;
	window.addEventListener('coret-shop-updated', () => {
		cache.clear();
		for (const callbacks of subscribers.values()) {
			for (const callback of callbacks) callback();
		}
	});
}

function loadCosmetics(userId: string, force = false): Promise<CoretProfileCosmetics> {
	const cached = cache.get(userId);
	if (!force && cached && cached.expiresAt > Date.now()) return Promise.resolve(cached.value);
	const existing = pending.get(userId);
	if (existing) return existing;
	let resolve!: (value: CoretProfileCosmetics) => void;
	let reject!: (reason: unknown) => void;
	const request = new Promise<CoretProfileCosmetics>((promiseResolve, promiseReject) => {
		resolve = promiseResolve;
		reject = promiseReject;
	});
	pending.set(userId, request);
	deferred.set(userId, {resolve, reject});
	queued.add(userId);
	if (!batchScheduled) {
		batchScheduled = true;
		queueMicrotask(() => void flushCosmeticsBatch());
	}
	return request;
}

export function useCoretProfileCosmetics(userId: string): CoretProfileCosmetics | null {
	const [cosmetics, setCosmetics] = useState<CoretProfileCosmetics | null>(() => {
		const cached = cache.get(userId);
		return cached && cached.expiresAt > Date.now() ? cached.value : null;
	});
	useEffect(() => {
		ensureShopListener();
		let active = true;
		const load = (force = false) => {
			void loadCosmetics(userId, force)
				.then((value) => active && setCosmetics(value))
				.catch(() => {});
		};
		load();
		const callbacks = subscribers.get(userId) ?? new Set<() => void>();
		const onShopUpdate = () => load(true);
		callbacks.add(onShopUpdate);
		subscribers.set(userId, callbacks);
		return () => {
			active = false;
			callbacks.delete(onShopUpdate);
			if (callbacks.size === 0) subscribers.delete(userId);
		};
	}, [userId]);
	return cosmetics;
}

function animationClass(animation: CoretShopAnimation | undefined): string {
	if (!animation || animation === 'none') return '';
	return {
		pulse: styles.animation_pulse,
		shimmer: styles.animation_shimmer,
		orbit: styles.animation_orbit,
		float: styles.animation_float,
		glow: styles.animation_glow,
		bounce: styles.animation_bounce,
		breathe: styles.animation_breathe,
		swing: styles.animation_swing,
		spin: styles.animation_spin,
		zoom: styles.animation_zoom,
		flicker: styles.animation_flicker,
		wave: styles.animation_wave,
		slide_left: styles.animation_slide_left,
		slide_right: styles.animation_slide_right,
		slide_up: styles.animation_slide_up,
		slide_down: styles.animation_slide_down,
		fade: styles.animation_fade,
		rainbow: styles.animation_rainbow,
		hue: styles.animation_hue,
		gradient_left: styles.animation_gradient_left,
		gradient_right: styles.animation_gradient_right,
		blur: styles.animation_blur,
		sparkle: styles.animation_sparkle,
		heartbeat: styles.animation_heartbeat,
		jitter: styles.animation_jitter,
		tilt: styles.animation_tilt,
		flip: styles.animation_flip,
		elastic: styles.animation_elastic,
		smoke: styles.animation_smoke,
		neon: styles.animation_neon,
	}[animation];
}

export function CoretProfileDecoration({
	userId,
	children,
	className,
}: {
	userId: string;
	children: React.ReactNode;
	className?: string;
}) {
	const cosmetics = useCoretProfileCosmetics(userId);
	const frame = cosmetics?.profile_frame;
	const effect = cosmetics?.profile_effect;
	const style = {
		'--coret-frame-accent': frame?.accent ?? effect?.accent ?? '#7c5cff',
		'--coret-frame-secondary': frame?.secondary ?? effect?.secondary ?? '#22d3ee',
	} as React.CSSProperties;
	return (
		<div
			className={`${styles.wrapper} ${animationClass(effect?.animation ?? frame?.animation)} ${className ?? ''}`}
			style={style}
		>
			{effect ? <div className={styles.effect} /> : null}
			{effect?.asset_url ? <img className={styles.effectAsset} src={effect.asset_url} alt="" /> : null}
			{children}
			{cosmetics?.collectibles?.length ? (
				<div className={styles.collectibleShelf} aria-label="Colecionáveis">
					{cosmetics.collectibles.map((item) =>
						item.asset_url ? (
							<img key={item.id} src={item.asset_url} title={`${item.name} · ${item.rarity}`} alt={item.name} />
						) : (
							<span key={item.id} title={item.name}>
								🏆
							</span>
						),
					)}
				</div>
			) : null}
			{cosmetics?.social_rewards?.length ? (
				<div className={styles.socialShelf} aria-label="Reconhecimentos sociais">
					{cosmetics.social_rewards.slice(0, 3).map((reward) => (
						<span
							key={reward.id}
							title={
								reward.message ||
								(reward.type === 'recognition'
									? 'Reconhecimento recebido'
									: reward.type === 'compliment'
										? 'Elogio recebido'
										: 'Cartão de amizade')
							}
						>
							{reward.type === 'recognition' ? '🎁' : reward.type === 'compliment' ? '💜' : '💌'}
						</span>
					))}
				</div>
			) : null}
			{frame ? <div className={styles.profileFrame} /> : null}
			{frame?.asset_url ? <img className={styles.profileFrameAsset} src={frame.asset_url} alt="" /> : null}
		</div>
	);
}

export function CoretAvatarDecoration({
	userId,
	children,
	speaking = false,
	className,
}: {
	userId: string;
	children: React.ReactNode;
	speaking?: boolean;
	className?: string;
}) {
	const cosmetics = useCoretProfileCosmetics(userId);
	const frame = cosmetics?.avatar_frame;
	if (!frame && !speaking) return <>{children}</>;
	const style = {
		'--coret-frame-accent': frame?.accent ?? '#23a55a',
		'--coret-frame-secondary': frame?.secondary ?? '#23a55a',
	} as React.CSSProperties;
	const speakingClass = speaking ? (frame ? styles.avatarSpeakingWithFrame : styles.avatarSpeakingWithoutFrame) : '';
	return (
		<span
			className={`${styles.avatarWrapper} ${speakingClass} ${className ?? ''}`}
			style={style}
			data-coret-avatar-frame={frame ? 'true' : 'false'}
		>
			{children}
			{frame ? (
				<span className={`${styles.avatarFrameLayer} ${animationClass(frame.animation)}`} aria-hidden>
					{frame.asset_url ? (
						<img className={styles.avatarFrameAsset} src={frame.asset_url} alt="" />
					) : (
						<span className={styles.avatarFrame} />
					)}
				</span>
			) : null}
		</span>
	);
}

export function CoretDisplayNameDecoration({
	userId,
	children,
	className,
}: {
	userId: string;
	children: React.ReactNode;
	className?: string;
}) {
	const item = useCoretProfileCosmetics(userId)?.nameplate;
	if (!item) return <>{children}</>;
	const style = {
		'--coret-nameplate-accent': item.accent,
		'--coret-nameplate-secondary': item.secondary,
		'--coret-nameplate-image': item.asset_url ? `url(${item.asset_url})` : 'none',
		'--coret-nameplate-font': {
			default: 'inherit',
			serif: 'Georgia, serif',
			monospace: 'ui-monospace, monospace',
			gothic: 'Impact, Haettenschweiler, sans-serif',
			rounded: 'Nunito, ui-rounded, sans-serif',
			display: 'Trebuchet MS, sans-serif',
		}[item.font ?? 'default'],
	} as React.CSSProperties;
	return (
		<span className={`${styles.displayName} ${animationClass(item.animation)} ${className ?? ''}`} style={style}>
			{children}
		</span>
	);
}
